import React, { useState } from 'react';
import {
  Form,
  TextFieldControl,
  ModalView,
  Stack,
  withTranslation,
  TFunction,
  Information,
} from '@piwikpro/ui-components';
import { UserGroups, UserGroup } from '@piwikpro/user-groups-crate';
import { FormValidator } from '@piwikpro/form-crate';
import { errorBoundary, withInject } from '@piwikpro/platform';
import { RouterService } from '@piwikpro/router-crate';

interface AddExternalIDProps {
  validation: FormValidator
  t: TFunction
  Title: typeof React.Component
  routerService: RouterService
  userGroups: UserGroups
  userGroup: UserGroup
  filters: any
}

const AddExternalID: React.FC<AddExternalIDProps> = ({
  validation,
  t,
  Title,
  routerService,
  userGroups,
  userGroup,
  filters,
}) => {
  const [externalGroupUpdateError, setExternalGroupUpdateError] = useState();
  const handleClose = () => {
    routerService.dismissAdministrationOverlay();
  };

  const handleSubmit = async (values, {
    setSubmitting,
    setFieldValue,
  }) => {
    try {
      const externalGroupID = values.externalGroupID.trim();
      await userGroups.update(userGroup.id, {
        external_id: externalGroupID,
        name: userGroup.attributes.name,
      });

      handleClose();

      await userGroups.find(filters);
      await userGroups.findOne(userGroup.id);
    } catch (err: any) {
      if (err.errors?.external_id) {
        setExternalGroupUpdateError(err.errors.external_id);
        setFieldValue(
          Object.keys(values)[0],
          userGroup.attributes.external_id,
          false,
        );
        setSubmitting(false);
      }
    }
  };

  return (
    <Title part={t('user-groups.assign-external-id.title')} subset>
      <Form
        id="assign-external-id-form"
        initialValues={{
          externalGroupID: userGroup.attributes.external_id,
        }}
        validationSchema={validation.createValidationSchema(validators => ({
          externalGroupID: validators.userGroups.externalId,
        }))}
        onSubmit={handleSubmit}
      >
        <ModalView
          title={t('user-groups.assign-external-id.title')}
          actions={(
            <Stack spacing="narrow" horizontalAlignment="right" verticalAlignment="center">
              <Form.Cancel onClick={() => handleClose()} />
              <Form.Submit text={t('user-groups.addUsers.button.submit')} />
            </Stack>
          )}
        >
          <Stack vertical fullWidth>
            {externalGroupUpdateError && (
              <Stack.Item fill>
                <Information type="error">
                  {externalGroupUpdateError}
                </Information>
              </Stack.Item>
            )}
            <Stack.Item fill>
              <TextFieldControl
                name="externalGroupID"
                autoFocus
                required
                label={t('user-groups.assign-external-id.fieldTitle')}
              />
            </Stack.Item>
          </Stack>
        </ModalView>
      </Form>
    </Title>
  );
};

export default withInject({
  validation: 'FormCrate.validation',
  userGroups: 'UserGroupsCrate.userGroups',
  Title: 'TitleCrate.components.Title',
  routerService: 'RouterCrate.router',
})(withTranslation(['administration', 'platform'])(
  errorBoundary(AddExternalID),
));
